// src/utils/supabaseIntegration.js - Integration with your existing Supabase setup
import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

console.log('🔐 Supabase initialization:', {
  hasUrl: !!supabaseUrl,
  hasKey: !!supabaseKey,
  url: supabaseUrl ? supabaseUrl.substring(0, 20) + '...' : 'missing',
});

if (!supabaseUrl || !supabaseKey) {
  console.error('❌ Missing Supabase environment variables');
  console.log('REACT_APP_SUPABASE_URL:', !!supabaseUrl);
  console.log('REACT_APP_SUPABASE_ANON_KEY:', !!supabaseKey);
}

export const supabase = createClient(supabaseUrl, supabaseKey);

console.log('🔐 Supabase client created:', !!supabase);

// Google Sheets Integration
export const fetchGoogleSheetsData = async (sheetId) => {
  try {
    // Use your existing Supabase function or Netlify function
    const { data, error } = await supabase.functions.invoke(
      'google-sheets-proxy',
      {
        body: { sheetId },
      }
    );

    if (error) throw error;
    return data;
  } catch (error) {
    // Fallback to Netlify function if Supabase function fails
    console.log('Falling back to Netlify function...');
    const response = await fetch(
      `/.netlify/functions/google-sheets-proxy?sheetId=${sheetId}`
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch sheet data: ${response.status}`);
    }

    return await response.json();
  }
};

// Portal Configuration Management
export const savePortalConfigToSupabase = async (userId, config) => {
  try {
    const { data, error } = await supabase
      .from('portal_configs')
      .upsert({
        user_id: userId,
        portal_id: config.portalId,
        company_name: config.companyName,
        primary_color: config.primaryColor,
        background_color: config.backgroundColor,
        logo_url: config.logoUrl,
        custom_message: config.customMessage,
        show_estimated_completion: config.showEstimatedCompletion,
        show_priority: config.showPriority,
        show_notes: config.showNotes,
        allow_client_updates: config.allowClientUpdates,
        config_data: config, // Store full config as JSON
        updated_at: new Date().toISOString(),
      })
      .select();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error saving portal config to Supabase:', error);
    throw error;
  }
};

// Sheet Configuration Management
export const saveSheetConfigToSupabase = async (userId, config) => {
  try {
    const { data, error } = await supabase
      .from('sheet_configs')
      .upsert({
        user_id: userId,
        sheet_id: config.sheetId,
        sheet_url: config.sheetUrl,
        column_mapping: config.columnMapping,
        setup_method: config.setupMethod,
        detected_headers: config.detectedHeaders,
        confidence: config.confidence,
        config_data: config, // Store full config as JSON
        updated_at: new Date().toISOString(),
      })
      .select();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error saving sheet config to Supabase:', error);
    throw error;
  }
};

// Load user configurations
export const loadUserConfigurations = async (userId) => {
  try {
    const [portalResult, sheetResult] = await Promise.all([
      supabase
        .from('portal_configs')
        .select('*')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false })
        .limit(1),
      supabase
        .from('sheet_configs')
        .select('*')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false })
        .limit(1),
    ]);

    return {
      portalConfig: portalResult.data?.[0]?.config_data || null,
      sheetConfig: sheetResult.data?.[0]?.config_data || null,
    };
  } catch (error) {
    console.error('Error loading user configurations:', error);
    return { portalConfig: null, sheetConfig: null };
  }
};

// Client Data Management
export const saveClientDataToSupabase = async (
  userId,
  portalId,
  clientData
) => {
  try {
    // Delete existing client data for this portal
    await supabase
      .from('client_data')
      .delete()
      .eq('user_id', userId)
      .eq('portal_id', portalId);

    // Insert new client data
    const clientRecords = clientData.map((client) => ({
      user_id: userId,
      portal_id: portalId,
      client_id: client.id,
      name: client.name,
      email: client.email,
      status: client.status,
      priority: client.priority,
      date: client.date,
      notes: client.notes,
      raw_data: client.rawData,
      updated_at: new Date().toISOString(),
    }));

    const { data, error } = await supabase
      .from('client_data')
      .insert(clientRecords)
      .select();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error saving client data to Supabase:', error);
    throw error;
  }
};

// Load client data from Supabase
export const loadClientDataFromSupabase = async (userId, portalId) => {
  try {
    const { data, error } = await supabase
      .from('client_data')
      .select('*')
      .eq('user_id', userId)
      .eq('portal_id', portalId)
      .order('updated_at', { ascending: false });

    if (error) throw error;

    return data.map((record) => ({
      id: record.client_id,
      name: record.name,
      email: record.email,
      status: record.status,
      priority: record.priority,
      date: record.date,
      notes: record.notes,
      rawData: record.raw_data,
      headers: record.raw_data, // You might want to store headers separately
    }));
  } catch (error) {
    console.error('Error loading client data from Supabase:', error);
    return [];
  }
};

// Get client by email for portal access
export const getClientByEmail = async (portalId, email) => {
  try {
    const { data, error } = await supabase
      .from('client_data')
      .select(
        `
        *,
        portal_configs!inner(*)
      `
      )
      .eq('portal_configs.portal_id', portalId)
      .ilike('email', email)
      .limit(1);

    if (error) throw error;

    if (!data || data.length === 0) {
      return null;
    }

    const record = data[0];
    return {
      client: {
        id: record.client_id,
        name: record.name,
        email: record.email,
        status: record.status,
        priority: record.priority,
        date: record.date,
        notes: record.notes,
      },
      portalConfig: record.portal_configs.config_data,
    };
  } catch (error) {
    console.error('Error getting client by email:', error);
    return null;
  }
};

// Portal Analytics
export const getPortalAnalytics = async (userId, portalId) => {
  try {
    const { data, error } = await supabase
      .from('client_data')
      .select('status')
      .eq('user_id', userId)
      .eq('portal_id', portalId);

    if (error) throw error;

    const statusCounts = {};
    let completed = 0;
    let inProgress = 0;
    let pending = 0;

    data.forEach((record) => {
      const status = (record.status || 'Unknown').toLowerCase();
      statusCounts[status] = (statusCounts[status] || 0) + 1;

      if (status.includes('complete') || status.includes('done')) {
        completed++;
      } else if (status.includes('progress') || status.includes('active')) {
        inProgress++;
      } else if (status.includes('pending') || status.includes('queue')) {
        pending++;
      }
    });

    return {
      totalClients: data.length,
      completed,
      inProgress,
      pending,
      statusBreakdown: statusCounts,
    };
  } catch (error) {
    console.error('Error getting portal analytics:', error);
    return {
      totalClients: 0,
      completed: 0,
      inProgress: 0,
      pending: 0,
      statusBreakdown: {},
    };
  }
};

// Database schema suggestions for your Supabase setup:
/*
-- Portal Configurations Table
CREATE TABLE portal_configs (
  id UUID DEFAULT gen_random_uuid() PRIMARY KEY,
  user_id UUID NOT NULL REFERENCES auth.users(id) ON DELETE CASCADE,
  portal_id TEXT NOT NULL UNIQUE,
  company_name TEXT,
  primary_color TEXT DEFAULT '#3B82F6',
  background_color TEXT DEFAULT '#F9FAFB',
  logo_url TEXT,
  custom_message TEXT,
  show_estimated_completion BOOLEAN DEFAULT true,
  show_priority BOOLEAN DEFAULT true,
  show_notes BOOLEAN DEFAULT true,
  allow_client_updates BOOLEAN DEFAULT false,
  config_data JSONB, -- Store full config as JSON
  created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
  updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
);

-- Sheet Configurations Table
CREATE TABLE sheet_configs (
  id UUID DEFAULT gen_random_uuid() PRIMARY KEY,
  user_id UUID NOT NULL REFERENCES auth.users(id) ON DELETE CASCADE,
  sheet_id TEXT NOT NULL,
  sheet_url TEXT NOT NULL,
  column_mapping JSONB NOT NULL,
  setup_method TEXT,
  detected_headers TEXT[],
  confidence INTEGER,
  config_data JSONB, -- Store full config as JSON
  created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
  updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
);

-- Client Data Table
CREATE TABLE client_data (
  id UUID DEFAULT gen_random_uuid() PRIMARY KEY,
  user_id UUID NOT NULL REFERENCES auth.users(id) ON DELETE CASCADE,
  portal_id TEXT NOT NULL REFERENCES portal_configs(portal_id) ON DELETE CASCADE,
  client_id TEXT NOT NULL,
  name TEXT NOT NULL,
  email TEXT NOT NULL,
  status TEXT,
  priority TEXT,
  date TEXT,
  notes TEXT,
  raw_data JSONB, -- Store original row data
  created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
  updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
  UNIQUE(portal_id, client_id)
);

-- Indexes for better performance
CREATE INDEX idx_portal_configs_user_id ON portal_configs(user_id);
CREATE INDEX idx_portal_configs_portal_id ON portal_configs(portal_id);
CREATE INDEX idx_sheet_configs_user_id ON sheet_configs(user_id);
CREATE INDEX idx_client_data_user_id ON client_data(user_id);
CREATE INDEX idx_client_data_portal_id ON client_data(portal_id);
CREATE INDEX idx_client_data_email ON client_data(email);

-- Row Level Security (RLS) policies
ALTER TABLE portal_configs ENABLE ROW LEVEL SECURITY;
ALTER TABLE sheet_configs ENABLE ROW LEVEL SECURITY;
ALTER TABLE client_data ENABLE ROW LEVEL SECURITY;

-- Users can only access their own data
CREATE POLICY "Users can manage their own portal configs" ON portal_configs
  FOR ALL USING (auth.uid() = user_id);

CREATE POLICY "Users can manage their own sheet configs" ON sheet_configs
  FOR ALL USING (auth.uid() = user_id);

CREATE POLICY "Users can manage their own client data" ON client_data
  FOR ALL USING (auth.uid() = user_id);
*/

const supabaseIntegration = {
  fetchGoogleSheetsData,
  savePortalConfigToSupabase,
  saveSheetConfigToSupabase,
  loadUserConfigurations,
  saveClientDataToSupabase,
  loadClientDataFromSupabase,
  getClientByEmail,
  getPortalAnalytics,
};

export default supabaseIntegration;
